// src/pages/ComingSoon.jsx
import React from 'react';
import { getApexLogoAssetPath, getIslandFullLogoSrc } from 'alakai-aloha-shared';
import { ArrowRight, Orbit, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLink from '../components/shared/AppLink';
import { APEX_FUTURE_PRODUCT_LINKS } from '../lib/apexContent';
import { getRuntimeSiteContext } from '../lib/siteContext';

const ISLAND_FEATURE_COPY = {
  'photo-planner': {
    eyebrow: 'Photo Planner',
    title: (islandConfig) => `${islandConfig.shortTitle} photo planner is coming soon.`,
    body: (islandConfig) =>
      `The ${islandConfig.shortTitle} guide, itinerary planner, stay recommender, beach conditions, weather, and sunrise/sunset tools are live. The photo planner is the remaining island-specific tool still being built for ${islandConfig.title}.`,
  },
  'featured-itineraries': {
    eyebrow: 'Sample Itineraries',
    title: (islandConfig) => `${islandConfig.shortTitle} sample itineraries are coming soon.`,
    body: (islandConfig) =>
      `The ${islandConfig.shortTitle} trip planner is live, but the public sample itinerary gallery is still Kauai-only while we prepare examples for the newer islands.`,
  },
  'multi-island-planner': {
    eyebrow: 'Multi-Island Planner',
    title: () => 'Multi-island planning is coming soon.',
    body: () =>
      'Each island planner works as a single-island experience today. Cross-island trip logic, transfer days, and shared itinerary planning are still on the roadmap.',
  },
};

function getIslandFeatureCopy(featureId, islandConfig) {
  const featureCopy = ISLAND_FEATURE_COPY[featureId];
  if (featureCopy) {
    return {
      eyebrow: featureCopy.eyebrow,
      title: featureCopy.title(islandConfig),
      body: featureCopy.body(islandConfig),
    };
  }

  return {
    eyebrow: 'Coming Soon',
    title: 'This feature is coming soon.',
    body: `This specific ${islandConfig.shortTitle} feature is not available yet, but the main island guide and planning tools are live.`,
  };
}

const ComingSoonPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isApexHost, islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
    const heroLogoSrc = isApexHost
      ? getApexLogoAssetPath('full', 'white', 'svg')
      : getIslandFullLogoSrc(islandConfig.key);
    const featureId = searchParams.get('feature');
    const apexFeature = isApexHost
      ? APEX_FUTURE_PRODUCT_LINKS.find((item) => item.id === featureId)
      : null;
    const islandFeatureCopy = React.useMemo(
      () => getIslandFeatureCopy(featureId, islandConfig),
      [featureId, islandConfig],
    );

    const handleGoBack = () => {
        navigate(-1); // Go back one page in history
    };

    if (isApexHost) {
      return (
        <div className="relative z-0 px-5 pb-10 pt-4 sm:px-6">
          <div className="mx-auto max-w-4xl">
            <div className="rounded-lg apex-panel px-6 py-8 text-center sm:p-10 lg:p-14">
              <p className="apex-kicker mx-auto">
                {apexFeature ? <Orbit className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                {apexFeature ? 'Future Apex Product' : 'Apex Roadmap'}
              </p>

              <h1 className="apex-display mt-6 text-5xl font-semibold text-white sm:text-6xl">
                {apexFeature ? apexFeature.label : 'Coming soon'}
              </h1>

              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/72">
                {apexFeature
                  ? apexFeature.description
                  : 'The island-specific editions are rolling out first. The shared cross-island planning layer is the next job for apex.'}
              </p>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/58 sm:text-base">
                {apexFeature
                  ? apexFeature.detail
                  : 'For now, apex remains the parent-brand front door, support surface, and legal layer above the island subdomains.'}
              </p>

              {apexFeature ? (
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {apexFeature.highlights.map((highlight) => (
                    <span key={highlight} className="apex-chip">
                      {highlight}
                    </span>
                  ))}
                </div>
              ) : null}

              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <button onClick={handleGoBack} className="apex-button-secondary">
                  Go back
                </button>
                <a href="/#future-products" className="apex-button-secondary">
                  Back to future products
                </a>
                <AppLink to="/support" className="apex-button-primary">
                  <span>Contact support</span>
                  <ArrowRight className="h-4 w-4" />
                </AppLink>
              </div>

              <div className="mt-12">
                <img src={heroLogoSrc} alt="Alaka'i Aloha Logo" className="mx-auto w-72 sm:w-80" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
          <div className="flex flex-col items-center justify-start min-h-screen text-white px-8 pt-4 pb-8 text-center relative z-0">
            {/* Message */}
            <div className="mt-0 sm:mt-4">
              <div className="h-px w-64 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-10 opacity-80"></div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.28em] text-white/70">
                {islandFeatureCopy.eyebrow}
              </p>
              <h1 className="text-4xl font-bold mb-4 text-white">
                {islandFeatureCopy.title}
              </h1>
              <p className="text-xl font-light tracking-wide mb-8 sm:mb-0 text-center text-white max-w-2xl mx-auto">
                {islandFeatureCopy.body}
              </p>
            
            {/* Navigation buttons */}
              <div className="space-y-4">
                <button
                  onClick={handleGoBack}
                  className="btn-primary-cta w-64 mx-auto"
                >
                  Go Back
                </button>
                <AppLink
                  to="/"
                  className="btn-secondary w-64 mx-auto"
                >
                  {isApexHost ? 'Return to Homepage' : 'Return to Homepage'}
                </AppLink>
                <AppLink
                  to={isApexHost ? '/support' : '/blog'}
                  className="btn-secondary w-64 mx-auto"
                >
                  {isApexHost ? 'Contact Support' : `Browse ${islandConfig.shortTitle}`}
                </AppLink>
              </div>              
            
            <div className="h-px w-64 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mt-10 opacity-80"></div>
            
            {/* Logo */}
            <div className="mt-12">
              <img src={heroLogoSrc} alt="Alaka'i Aloha Logo" className="w-96 mx-auto mb-4" />
            </div>

            </div>
          </div>
    );
};

export default ComingSoonPage;
